import type { RouteObject } from 'react-router-dom';

import { GuestRoute } from '@/app/router/GuestRoute';
import { ROUTES } from '@/config/routes';

import { lazy } from 'react';

/**
 * Loaded on demand, one chunk per screen.
 *
 * <p>Somebody opening the sign-in page should not download the sign-up form, the
 * activation flow and the dashboard to see two inputs and a button. Split here
 * rather than at the router so a feature owns how it is loaded.
 */
const ActivatePage = lazy(() =>
  import('./routes/ActivatePage').then((m) => ({ default: m.ActivatePage })),
);
const SetInvitedPasswordPage = lazy(() =>
  import('./routes/SetInvitedPasswordPage').then((m) => ({ default: m.SetInvitedPasswordPage })),
);
const ResendActivationPage = lazy(() =>
  import('./routes/ResendActivationPage').then((m) => ({ default: m.ResendActivationPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./routes/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./routes/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const OAuthCallbackPage = lazy(() =>
  import('./routes/OAuthCallbackPage').then((m) => ({ default: m.OAuthCallbackPage })),
);

const MagicLinkRequestPage = lazy(() =>
  import('./routes/MagicLinkRequestPage').then((m) => ({ default: m.MagicLinkRequestPage })),
);
const MagicLinkCallbackPage = lazy(() =>
  import('./routes/MagicLinkCallbackPage').then((m) => ({ default: m.MagicLinkCallbackPage })),
);

/*
 * NOT lazy. It is a guard, not a screen: it runs on the way to four routes and decides
 * whether they render at all, so loading it as a chunk would put a loader in front of every
 * one of them to answer a question that is already known.
 */
import { SignOutFirstRoute } from './routes/SignOutFirstRoute';

/**
 * Sign-in LEFT the quarantine on 3 October 2026. Registration has not.
 *
 * <p>`routes/SignInPage` is now the screen `/login` renders. It carries the previous product's React app's
 * current design — the split screen, the brand panel, the artwork — on this repository's
 * own request shapes, zod schemas and error mapping, which it already held while being
 * unrouted. `src/components/auth/LoginPage.tsx` is still on disk and no longer reachable;
 * it goes once the new screen has been used in anger, and until then it is the thing to
 * compare against.
 *
 * <p>`routes/SignUpPage` is in the position sign-in used to be in: written, tested, and not
 * what anybody sees. `AuthRoutes.SignUpRoute` is still the ported screen.
 */
const SignInPage = lazy(() =>
  import('./routes/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import('@/components/auth/AuthRoutes').then((m) => ({ default: m.SignUpRoute })),
);

/**
 * This feature's public surface.
 *
 * <p>The router imports these rather than reaching into `routes/`, so the route
 * table states the shape of the application and a feature stays free to
 * rearrange its own files. ESLint enforces the boundary.
 */
/**
 * Screens that bring their own full-page layout.
 *
 * <p>The ported sign-in and registration pages are whole compositions -- a
 * two-column grid, their own panel, their own footer. Putting them inside
 * `AuthLayout`, which centres its child in a `max-w-md` column, squeezed the
 * entire page into 448 pixels: everything present, nothing where it belongs.
 */
export const fullPageAuthRoutes: RouteObject[] = [
  /*
   * FOUR ROUTES A SIGNED-IN PERSON HAS NO BUSINESS ON, and none of them carries a token.
   *
   * Sign in, register, ask for a password link, ask for another activation link: every one
   * is the beginning of getting into an account, and a browser that is already in one has
   * nothing to do on any of them. Redirecting loses nothing, because the address carries
   * nothing — which is the whole distinction from the group below.
   *
   * `forgotPassword` and `resendActivation` were briefly in that group, on the reasoning
   * that they are errands for an account that may not be the one signed in. True, and it
   * does not follow: there is no token to swallow, so the cost of being wrong is a
   * dashboard instead of a form, and `SignOutFirstRoute` is a screen to read where a
   * redirect would do.
   *
   * Grouped under the guard rather than each page checking for itself, so the rule is
   * visible in the routing table and a fifth such screen joins it by being written on the
   * right side of this line. `GuestRoute` has why it does not wait for the session.
   */
  {
    element: <GuestRoute />,
    children: [
      { path: ROUTES.signIn, element: <SignInPage /> },
      { path: ROUTES.signUp, element: <SignUpPage /> },

      // The ported sign-in page's "Forgot Password?" routes here through the copied
      // navigation map, so this path is fixed by that rather than chosen.
      { path: ROUTES.forgotPassword, element: <ForgotPasswordPage /> },
      { path: ROUTES.resendActivation, element: <ResendActivationPage /> },

      // Asking for a sign-in link is the same kind of errand as asking for a
      // password one, and carries no token either -- so it belongs on this side
      // of the line for the reason the comment above gives.
      { path: ROUTES.magicLinkRequest, element: <MagicLinkRequestPage /> },
    ],
  },

  /*
   * THE ADDRESSES THAT CARRY A SINGLE-USE TOKEN — three of them, and only one still
   * behind the guard.
   *
   * None is redirected like the four above: the token is in the address, so sending
   * somebody to the dashboard swallows it with no explanation. They go back to their
   * inbox, click the same link, arrive at the dashboard again, and a link that works
   * perfectly looks broken.
   *
   * WHAT CHANGED ON 4 OCTOBER 2026, and it is `AC-63`. All three sat behind
   * `SignOutFirstRoute`, and a guard decides BEFORE the page it guards renders — so it
   * decided before anything could ask whether the link worked. An invitation that was
   * already dead said so, then said "Someone is already signed in" when another tab
   * signed in, then said it was dead again when that tab signed out. Nothing about the
   * link changed in that sequence; the one fact the person needed looked as though it
   * depended on who was signed in.
   *
   * So link validity outranks the session, and a page is the only place that order can
   * be expressed — `SetInvitedPasswordPage` runs the whole chain and renders the very
   * same `SignOutFirst` at step 6 of it. `ActivatePage` runs one too, inverted while the
   * link state is unknown, because it cannot ask without spending the link (`AC-63a`).
   *
   * `/reset-password` keeps the guard. It has no non-consuming check — its token is
   * validated by the submit — so on arrival the link's state is genuinely unknown and
   * there is nothing for validity to outrank.
   *
   * `oauthCallback` stays outside all of them: that page is mid-handshake, reached WITH a
   * session being established, so a guard keyed on the session is exactly the wrong thing
   * in front of it.
   */
  {
    element: <SignOutFirstRoute />,
    children: [{ path: ROUTES.resetPassword, element: <ResetPasswordPage /> }],
  },

  // Their own precedence chains, for the reason above. Each renders `SignOutFirst`
  // itself, at the point in its own order where the session is the actual obstacle.
  { path: ROUTES.activate, element: <ActivatePage /> },
  { path: ROUTES.setInvitedPassword, element: <SetInvitedPasswordPage /> },

  /*
   * The sign-in link, and it is in this group rather than behind the guard above --
   * corrected on 19 October 2026, having been put there first.
   *
   * It carries a single-use token like the three of them, and a signed-in browser is
   * genuinely in its way: spending it signs in somebody who may not be whoever is
   * already here. So it renders the same `SignOutFirst`, and leaves the token in the
   * address while it does, exactly as they do.
   *
   * What it cannot do is take that answer from a ROUTE GUARD, and this is the sharper
   * version of the reason `/activate` runs its own chain. `SignOutFirstRoute` renders
   * its child only while signed OUT -- so the moment this page succeeded and started a
   * session, the guard replaced it with "Someone is already signed in", naming the
   * account the person had just signed into. A page that creates a session cannot be
   * guarded on not having one. `oauthCallback` below says the same thing more briefly.
   */
  { path: ROUTES.magicLink, element: <MagicLinkCallbackPage /> },

  // Full page rather than inside AuthLayout: it either redirects immediately or
  // is a standalone outcome, and neither wants the narrow centred frame.
  { path: ROUTES.oauthCallback, element: <OAuthCallbackPage /> },
];

/**
 * Screens that expect the narrow centred frame.
 *
 * <p>Empty, and kept rather than deleted: activation and resend were written
 * against `AuthLayout` and moved out of it when they were given the ported
 * design, so this is the group a future screen written to this project's own
 * layout belongs in. The router already renders it, so adding one is a line here.
 */
export const authRoutes: RouteObject[] = [];
